import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_tornado-alley/artifacts/zdyn54g1_tornado_alley_trading_co_transparent_full_resolution.png";

const links = [
  { to: "/tee-club", label: "Tee Club" },
  { to: "/drops", label: "Past Drops" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Custom" },
];

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <nav className="fixed w-full z-50 backdrop-blur-xl bg-[#FDFBF7]/85 border-b-2 border-[#1C1C1C]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Tornado Alley Trading Co." className="h-12 md:h-16 w-auto object-contain" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-display text-xl tracking-wide uppercase transition-colors ${
                  isActive ? "text-[#D35400]" : "text-[#1C1C1C] hover:text-[#D35400]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <Link to="/cart" className="relative" data-testid="nav-cart">
            <ShoppingBag className="w-6 h-6" strokeWidth={2.2} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#C0392B] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {isAdmin && (
                <Link to="/admin" className="font-display text-lg uppercase text-[#2C3E50] hover:text-[#D35400]" data-testid="nav-admin">
                  Admin
                </Link>
              )}
              <Link to="/account" data-testid="nav-account">
                <User className="w-6 h-6" strokeWidth={2.2} />
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                className="text-sm font-semibold underline underline-offset-4 hover:text-[#D35400]"
                data-testid="nav-logout"
              >
                Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden md:block font-display text-xl uppercase hover:text-[#D35400]" data-testid="nav-login">
              Sign In
            </Link>
          )}

          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-[#1C1C1C] bg-[#FDFBF7] px-5 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-display text-2xl uppercase" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="font-display text-2xl uppercase text-[#2C3E50]">Admin</Link>}
              <Link to="/account" onClick={() => setOpen(false)} className="font-display text-2xl uppercase">Account</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left font-display text-2xl uppercase text-[#C0392B]">Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="font-display text-2xl uppercase">Sign In</Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
